/**
 * AdSlotReport — /admin/ad-slots/report/:placement
 *
 * Print-ready media kit for a single ad slot. Fetches lifetime impressions,
 * click totals, per-campaign breakdown, and renders a clean sheet that can
 * be printed or saved as PDF and handed to a potential ad buyer.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Printer, Download, TrendingUp, MousePointer, BarChart3, Eye, Calendar } from 'lucide-react';

const fmt = (n) => (n || 0).toLocaleString();
const pct = (n) => `${(n || 0).toFixed(2)}%`;

const AdSlotReport = () => {
  const { placement } = useParams();
  const { API } = useContext(AuthContext);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/ad-slots/${placement}/report`)
      .then((r) => { if (!cancelled) setReport(r.data); })
      .catch(() => { if (!cancelled) setReport(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [API, placement]);

  const printKit = () => window.print();

  const exportCsv = () => {
    if (!report) return;
    const rows = [['Campaign', 'Priority', 'Active', 'Impressions', 'Clicks', 'CTR %', 'Created']];
    (report.campaigns || []).forEach((c) => {
      rows.push([
        (c.name || '').replaceAll(',', ' '),
        c.priority || '', c.active ? 'yes' : 'no',
        c.impressions, c.clicks, c.ctr_pct,
        c.created_at || '',
      ]);
    });
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${placement}-media-kit.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background print:bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 print:py-4">
        {/* Toolbar — hidden on print */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <Link to="/admin/ad-slots" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> Back to slots
          </Link>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={exportCsv} className="rounded-full font-bold" data-testid="ad-report-csv">
              <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
            </Button>
            <Button size="sm" onClick={printKit} className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90" data-testid="ad-report-print">
              <Printer className="w-3.5 h-3.5 mr-1" /> Print media kit
            </Button>
          </div>
        </div>

        {loading && <div className="text-center py-16 text-sm text-muted-foreground">Loading report…</div>}
        {!loading && !report && <div className="text-center py-16 text-sm text-muted-foreground">Report unavailable.</div>}

        {!loading && report && (
          <article className="pro-card p-8 print:shadow-none print:border-0" data-testid="ad-report-sheet">
            {/* Header */}
            <header className="border-b border-border pb-5 mb-6">
              <div className="eyebrow text-primary">Media Kit</div>
              <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground mt-1" data-testid="ad-report-title">
                {report.label}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground max-w-2xl">{report.description}</p>
              <div className="mt-3 text-[11px] text-muted-foreground inline-flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Generated {new Date(report.generated_at).toLocaleString()}</span>
                <span>Slot key: <code className="font-mono">{report.placement}</code></span>
              </div>
            </header>

            {/* Big-picture stats */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="ad-report-stats">
              <StatTile icon={<TrendingUp className="w-3.5 h-3.5" />} label="Lifetime impressions" value={fmt(report.impressions)} />
              <StatTile icon={<MousePointer className="w-3.5 h-3.5" />} label="Total clicks"           value={fmt(report.clicks)} />
              <StatTile icon={<BarChart3 className="w-3.5 h-3.5" />}   label="Blended CTR"             value={pct(report.ctr_pct)} />
              <StatTile icon={<Eye className="w-3.5 h-3.5" />}         label="Active campaigns"        value={`${report.active_count} / ${report.campaign_count}`} />
            </section>

            {/* Value proposition (short pitch) */}
            <section className="mb-6 rounded-2xl border border-primary/30 bg-primary/5 p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">For advertisers</div>
              <p className="text-sm text-foreground mt-1 leading-relaxed">
                This placement has delivered <strong>{fmt(report.impressions)}</strong> impressions with a
                blended <strong>{pct(report.ctr_pct)}</strong> click-through rate. Your creative renders in
                front of Freeze Dry Academy&rsquo;s operator community — vetted professionals actively
                buying gear, ingredients, and services in the freeze-dry space.
              </p>
            </section>

            {/* Campaign breakdown */}
            <section>
              <h2 className="font-heading text-xl font-extrabold text-foreground mb-3">Campaign history</h2>
              {report.campaigns.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">No campaigns have run in this slot yet.</div>
              ) : (
                <div className="rounded-xl border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-[10px] uppercase tracking-widest font-bold">
                      <tr>
                        <th className="text-left px-3 py-2">Campaign</th>
                        <th className="text-left px-3 py-2">Status</th>
                        <th className="text-right px-3 py-2">Impressions</th>
                        <th className="text-right px-3 py-2">Clicks</th>
                        <th className="text-right px-3 py-2">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.campaigns.map((c) => (
                        <tr key={c.id} className="border-t border-border" data-testid={`ad-report-row-${c.id}`}>
                          <td className="px-3 py-2">
                            <div className="font-bold text-foreground">{c.name}</div>
                            <div className="text-[10px] text-muted-foreground">{c.priority} priority · created {c.created_at?.slice(0, 10)}</div>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${c.active ? 'bg-emerald-500/15 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>
                              {c.active ? 'Active' : 'Paused'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{fmt(c.impressions)}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmt(c.clicks)}</td>
                          <td className="px-3 py-2 text-right font-mono">{pct(c.ctr_pct)}</td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-border bg-muted/30 font-bold">
                        <td className="px-3 py-2">Total</td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2 text-right font-mono">{fmt(report.impressions)}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmt(report.clicks)}</td>
                        <td className="px-3 py-2 text-right font-mono">{pct(report.ctr_pct)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <footer className="mt-8 pt-4 border-t border-border text-[11px] text-muted-foreground text-center">
              Prepared by Freeze Dry Academy · hello@freezedry.academy · Data covers the lifetime of this slot.
            </footer>
          </article>
        )}
      </div>

      <style>{`
        @media print {
          .pro-card { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
};

const StatTile = ({ icon, label, value }) => (
  <div className="rounded-xl border border-border bg-muted/40 p-3">
    <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground inline-flex items-center gap-1">{icon} {label}</div>
    <div className="text-xl font-black text-foreground leading-tight mt-1">{value}</div>
  </div>
);

export default AdSlotReport;